import type { ErrorEvent as SentryErrorEvent } from '@sentry/react';

// Facebook's in-app browser serves its logger as a separate script under its own scheme on
// Android, and inlines it into the host document on iOS — where Sentry then attributes the
// frames to our page URL, so the filename check alone cannot see it.
const FB_BRIDGE_SCRIPT_SCHEME = 'iabjs://';
// The one sink every observed variant funnels through, whichever way the script was served.
// No tach-ui code declares a function by this name.
const FB_BRIDGE_SINK = 'sendDataToNative';
// Some Facebook builds ship the logger minified, which leaves no usable frame at all: the iOS
// variant in JAVASCRIPT-REACT-TS arrives with functions `f` and `w` under our own /signup URL,
// so neither signal above can see it. What minification cannot strip is the API the script died
// reaching for. `window.webkit.messageHandlers` is WKWebView's JS→native bridge; it appears
// nowhere in tach-ui or in any installed dependency, so an error evaluating it is not ours.
const IOS_NATIVE_BRIDGE_API = 'window.webkit.messageHandlers';

/**
 * Whether an event was thrown by Facebook's in-app-browser bridge rather than by tach-ui.
 *
 * The Facebook app injects a navigation/performance logger into every page it renders and
 * postMessages the results to its own native bridge. When that bridge isn't there the injected
 * script throws: on iOS it reads `window.webkit.messageHandlers[...]` off `undefined`, on Android
 * it postMessages to a Java object teardown has already collected. Every frame belongs to
 * Facebook's script, entered from Facebook's own listeners, so there is nothing in this repo to
 * fix — the events are unactionable by construction.
 *
 * Matched on the bridge's own frames and the native API it calls, NOT on
 * `browser.name === 'Facebook'`: a genuine tach-ui error must still report for a user who arrived
 * from a Facebook ad. `/demo` is where these land because it is the ad landing page, so the
 * referral volume only grows from here.
 */
export function isFacebookInAppBrowserBridgeError(
  event: SentryErrorEvent
): boolean {
  return (
    event.exception?.values?.some(
      (value) =>
        value.value?.includes(IOS_NATIVE_BRIDGE_API) === true ||
        value.stacktrace?.frames?.some(
          (frame) =>
            frame.filename?.startsWith(FB_BRIDGE_SCRIPT_SCHEME) === true ||
            frame.function === FB_BRIDGE_SINK
        ) === true
    ) === true
  );
}
